import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { IconArrowLeft, IconArrowRight } from '$partials/icons/index.js';
import { useLocale } from '$contexts/locale.jsx';
import Button from '$partials/ui/button.jsx';
import RichText from './rich-text.jsx';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} layerPropTypes
 */
const NavigationLayer = ({
    links,
    openLayer,
    setOpenLayer
}) => {
    const {
        formatURI,
        translate: t
    } = useLocale();

    const getUrlSegment = (typename, item) => {
        let urlSegment;
        if (typename === 'BvaBusinessResourcePage') {
            urlSegment = 'resources';
        } else if (typename === 'BvaBusinessCaseStudyPage') {
            urlSegment = 'case-studies';
        } else if (typename === 'BvaBusinessLandingSubPage') {
            urlSegment = item?.parent?.bvaBusinessLandingPageCollection?.items?.[0]?.slug;
        } else {
            urlSegment = '';
        }
        return urlSegment;
    };

    return (
        <div className='h-full overflow-x-hidden '>
            {(links?.length > 0) &&
                <ul className='mb-70 relative'>
                    {links.map((link, i) => {
                        return (
                            <li
                                className='mb-25 last:mb-0 w-full sm:w-4/12'
                                key={i}>
                                {link?.dropdown ? (
                                    <div className='type-style-3'>
                                        <Button
                                            className={`slide-in flex flex-row gap-10 items-center w-full justify-between ${openLayer === 0 ? 'translate-x-0' : '-translate-x-[110dvw]'}`}
                                            onClick={() => setOpenLayer(i + 1)}
                                            tabIndex={openLayer === 0 ? 0 : -1}>
                                            {link?.title}
                                            <IconArrowRight
                                                className='ml-10 h-[13px]'
                                                stroke='white' />
                                        </Button>

                                        {link?.collection && (
                                            <div className={`overflow-y-auto absolute top-0 right-0 w-full slide-in ${openLayer === (i + 1) ? 'translate-x-0 block' : 'translate-x-[110dvw]'}`}>
                                                {link?.collection?.map((linkBlock, j) => {
                                                    const headerLinkUrl = formatURI(linkBlock?.headerLink?.slug);
                                                    return (
                                                        <ul
                                                            className='mb-70 last:mb-0 overflow-y-auto'
                                                            key={`linkBlock-${j}`}>
                                                            <li className='type-style-3 mb-30'>
                                                                <a
                                                                    href={headerLinkUrl}
                                                                    key={`linkBlock-${j}`}
                                                                    tabIndex={openLayer === (i + 1) ? 0 : -1}>
                                                                    {linkBlock?.headerLink?.title}
                                                                </a>
                                                            </li>

                                                            {linkBlock?.subLinksCollection?.items?.map((subLink, k) => {
                                                                if (subLink?.body) {
                                                                    return (
                                                                        <li
                                                                            className='mb-30 last:mb-0'
                                                                            key={`subLink-${k}`}
                                                                            tabIndex={openLayer === (i + 1) ? 0 : -1}>
                                                                            <RichText
                                                                                className='type-style-7'
                                                                                key={k}
                                                                                {...subLink?.body} />
                                                                        </li>
                                                                    );
                                                                } else {
                                                                    const segment = getUrlSegment(subLink?.__typename, subLink);
                                                                    const slug = subLink?.slug;
                                                                    const sublinkUrl = formatURI(`${segment}/${slug}`);

                                                                    // TODO: find out why parent page segment is undefined.
                                                                    return (
                                                                        <li
                                                                            className='mb-30 last:mb-0'
                                                                            key={`subLink-${k}`}>
                                                                            <a
                                                                                className='type-style-7'
                                                                                href={sublinkUrl}
                                                                                // href={`/${getUrlSegment(subLink?.__typename, subLink)}/${subLink?.slug}`}
                                                                                key={`subLink-${k}`}
                                                                                tabIndex={openLayer === (i + 1) ? 0 : -1}>
                                                                                {subLink?.title}
                                                                            </a>
                                                                        </li>
                                                                    );
                                                                }
                                                            })}
                                                        </ul>
                                                    );
                                                })}
                                            </div>
                                        )}

                                    </div>
                                ) : (
                                    <div className='w-full'>
                                        <a
                                            className={`w-full flex slide-in type-style-3 ${openLayer === 0 ? 'translate-x-0' : '-translate-x-[110dvw]'}`}
                                            href={link?.collection?.__typename === 'BvaBusinessContactListingPage' ? formatURI('contact') : formatURI(link?.collection?.slug)}
                                            tabIndex={openLayer === 0 ? 0 : -1}>
                                            {link?.title}
                                        </a>
                                    </div>
                                )
                                }
                            </li>
                        );
                    })}
                </ul>
            }

            <div className={`slide-in w-full sm:w-8/12 ${openLayer === 0 ? ' translate-x-0' : '-translate-x-[110dvw]'}`}>
                <a
                    className='w-full flex flex-row items-center gap-10 type-style-7'
                    href={formatURI('contact')}
                    tabIndex={openLayer === 0 ? 0 : -1}>
                    {t('Get In Touch')}
                    <IconArrowRight
                        className='ml-10 h-[13px]'
                        stroke='currentColor' />
                </a>
            </div>
        </div>
    );
};

/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} props
 */
const MobileNavigation = ({ menu }) => {
    const dialogRef = useRef();

    const [openLayer, setOpenLayer] = useState(0);

    const { translate: t } = useLocale();

    const close = () => {
        if (dialogRef.current) {
            dialogRef.current.close();
        }
        clearAllBodyScrollLocks();
        setOpenLayer(0);
    };

    const open = () => {
        if (dialogRef.current) {
            disableBodyScroll(dialogRef.current);
            dialogRef.current.showModal();
        }
    };

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') { close(); }
        };

        window.addEventListener('keydown', handleKeyDown, { passive: true });
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    return (
        <>
            <button
                aria-label= {t('Open navigation')}
                onClick={open}
                type='button'>
                <svg
                    style={{ width: '24px', height: '18px' }}
                    viewBox='0 0 24 18'
                    xmlns='http://www.w3.org/2000/svg'>
                    <path
                        d='M24 0.324951H0V1.67495H24V0.324951Z'
                        fill='currentColor'
                        stroke='currentColor' />
                    <path
                        d='M24 8.32495H0V9.67495H24V8.32495Z'
                        fill='currentColor'
                        stroke='currentColor' />
                    <path
                        d='M24 16.325H0V17.6749H24V16.325Z'
                        fill='currentColor'
                        stroke='currentColor' />
                </svg>
            </button>

            <dialog
                className='fixed inset-0 w-full h-full layout-container max-h-[100dvh] m-0 pt-30 pb-35 overflow-hidden bg-orange text-white text-left'
                ref={dialogRef}>
                {/* Back / Close */}
                <div className='px-10 sm:px-5 flex justify-between mb-35'>
                    <Button
                        aria-label= {t('Back navigation')}
                        className={`${openLayer > 0 ? '' : 'hidden'}`}
                        onClick={() => setOpenLayer(0)}
                        renderIcon={
                            <IconArrowLeft
                                className='ml-10 h-[13px]'
                                stroke='currentColor' />}
                        type='button'>
                    </Button>

                    <div />

                    <button
                        aria-label= {t('Close navigation')}
                        onClick={close}
                        type='button'>
                        <svg
                            style={{ width: '18px', height: '18px' }}
                            viewBox='0 0 18 18'
                            xmlns='http://www.w3.org/2000/svg'>
                            <path
                                d='M16.9706 -0.000103474L0 16.9705L0.954594 17.9251L17.9252 0.954491L16.9706 -0.000103474Z'
                                fill='currentColor' />
                            <path
                                d='M17.9627 17.0082L0.992188 0.0375977L0.0375931 0.992192L17.0082 17.9628L17.9627 17.0082Z'
                                fill='currentColor' />
                        </svg>
                    </button>
                </div>

                {/* Main links */}
                <nav className='px-10 sm:px-5 h-full relative'>
                    <h3
                        className='type-style-3 mb-70'>
                        {t('Go to')}
                    </h3>

                    <NavigationLayer
                        links={menu}
                        openLayer={openLayer}
                        setOpenLayer={setOpenLayer} />
                </nav>
            </dialog>
        </>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const layerPropTypes = {
    links: PropTypes.array,
    openLayer: PropTypes.number,
    setOpenLayer: PropTypes.func
};

const propTypes = {
    menu: PropTypes.array
};

MobileNavigation.propTypes = propTypes;
NavigationLayer.propTypes = layerPropTypes;

export default MobileNavigation;
